import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  styled,
  tableCellClasses,
  TablePagination,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Pie,
  PieChart,
  Cell,
} from "recharts";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import SelectInput from "../../Components/InputComponents/SelectInput";
import TextInput from "../../Components/InputComponents/TextInput";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const COLORS = ["#0088FE", "#FF8042", "#00C49F", "#FFBB28"];

const StatCard = React.memo(({ title, value, Icon, color }) => (
  <Card
    sx={{
      display: "flex",
      flexDirection: "column",
      boxShadow: "none",
      // border: "2px solid #f0f0f0",
      borderRadius: 2,
      height: "100%",
      bgcolor: color || "#f0f0f0",
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.34)",
    }}
  >
    <CardContent
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Geist, sans-serif",
            fontSize: "18px",
            color: "#1c1c1c",
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            background: "white",
            border: "2px solid lightgrey",
            borderRadius: "50%",
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {Icon}
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: "Geist, sans-serif",
          fontSize: "22px",
          color: "#212121",
          fontWeight: 600,
        }}
      >
        {value ?? "--"}
      </Typography>
    </CardContent>
  </Card>
));

const CandidatePerformanceTable = ({ data }) => {
  return (
    <TableContainer
      sx={{
        border: "1px solid lightgray",
        borderRadius: "10px",
        maxWidth: "100vw",
        overflowX: "auto",
        margin: "auto",
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableCell sx={{ width: "50px" }}>#</StyledTableCell>
            <StyledTableCell>Candidate Name</StyledTableCell>
            <StyledTableCell sx={{ textAlign: "center" }}>
              Interviews Attended{" "}
            </StyledTableCell>
            <StyledTableCell sx={{ textAlign: "center" }}>
              Total Applications{" "}
            </StyledTableCell>
            <StyledTableCell sx={{ textAlign: "center" }}>
              Hired Status{" "}
            </StyledTableCell>
            <StyledTableCell>Hired Job Title</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((candidate, idx) => (
            <StyledTableRow key={idx}>
              <StyledTableCell>{idx + 1}</StyledTableCell>
              <StyledTableCell>{candidate?.CI_Name}</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center" }}>
                {candidate?.InterviewsAttended}
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center" }}>
                {candidate?.TotalApplications}
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center" }}>
                {candidate?.HiredStatus}
              </StyledTableCell>
              <StyledTableCell>
                {candidate?.HiredJobTitles ? candidate?.HiredJobTitles : "-"}
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ApplicationsChart = ({
  data1,
  data2,
  data3,
  data4,
  data5,


  RecruitmentStageslist,

  recruitmentStage,
  setRecruitmentStage,

  processName,
  setProcessName,

  startDate,
  setStartDate,
  endDate,
  setEndDate,

  JobList,
  selectedJob,
  setSelectedJob,
  processList,

}) => {
  return (
    <Paper sx={{ boxShadow: "none" }}>
      <Grid container>
        <Grid item xs={12} sm={12} md={6} my={1}>
          <Box
            sx={{
              border: "2px solid lightgrey",
              borderRadius: 2,
              p: 2,
              my: 1,
              mx: { xs: 0, md: 1 },
              minHeight: {
                xs: "0px",
                sm: "0px",
                md: "400px",
                lg: "400px",
                xl: "400px",
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: "Geist, sans-serif",
                fontSize: "18px",
                color: "#3e3e3e",
                fontWeight: 500,
                letterSpacing: "0.5px",
              }}
              mb={1}
            >
              Country in Process
            </Typography>

            {data1?.every((item) => item.jobs == 0) ? (
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                sx={{
                  minHeight: "300px", // matches chart height so it’s truly centered
                }}
              >
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: "40px",
                    background: "white",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  No data to show
                </Typography>
              </Grid>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  top: "15px",
                }}
              >
                <ResponsiveContainer width="100%" minHeight={300}>
                  <BarChart
                    data={data1}
                    layout="vertical"
                    margin={{ top: 5, right: 10, left: 30, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="ApplicationCount"
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                        fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <YAxis
                      type="category"
                      dataKey="Country"
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                        fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="ApplicationCount"
                      fill="#006CB8"
                      name="Application Count"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={12} md={6} my={1}>
          <Box
            sx={{
              border: "2px solid lightgrey",
              borderRadius: 2,
              p: 2,
              my: 1,
              mx: { xs: 0, md: 1 },
              minHeight: {
                xs: "0px",
                sm: "0px",
                md: "400px",
                lg: "400px",
                xl: "400px",
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: "Geist, sans-serif",
                fontSize: "18px",
                color: "#3e3e3e",
                fontWeight: 500,
                letterSpacing: "0.5px",
              }}
              mb={1}
            >
              Deployed by Country
            </Typography>
            {data2?.every((item) => item.jobs == 0) ? (
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                sx={{
                  minHeight: "300px", // matches chart height so it’s truly centered
                }}
              >
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: "40px", background: "white" }}
                >
                  No data to show
                </Typography>
              </Grid>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  right: "20px",
                  top: "15px",
                }}
              >
                <ResponsiveContainer width="100%" minHeight={300}>
                  <BarChart data={data2}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="Country"
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                        fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <YAxis
                      dataKey="ApplicationCount"
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                        fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="ApplicationCount"
                      fill="#006CB8"
                      name="Deployed Candidate Count"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={12} md={6} mb={1}>
          <Box
            sx={{
              border: "2px solid lightgrey",
              borderRadius: 2,
              p: 2,
              mb: 1,
              mx: { xs: 0, md: 1 },
              minHeight: {
                xs: "0px",
                sm: "0px",
                md: "400px",
                lg: "400px",
                xl: "400px",
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: "Geist, sans-serif",
                fontSize: "18px",
                color: "#3e3e3e",
                fontWeight: 500,
                letterSpacing: "0.5px",
              }}
              mb={1}
            >
              Profession Statistics
            </Typography>

            {data3?.every(
              (item) => item.DeployedCount == 0 && item.InProgressCount == 0
            ) ? (
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                sx={{
                  minHeight: "300px", // matches chart height so it’s truly centered
                }}
              >
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: "40px", background: "white" }}
                >
                  No data to show
                </Typography>
              </Grid>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  right: "20px",
                  top: "15px",
                }}
              >
                <ResponsiveContainer width="100%" minHeight={300}>
                  <BarChart data={data3}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="Profession"
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                        fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <YAxis
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                        fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="DeployedCount"
                      stackId="a"
                      fill="#82ca9d"
                      name="Deployed Count"
                    />
                    <Bar
                      dataKey="InProgressCount"
                      stackId="a"
                      fill="#8884d8"
                      name="In Progress Count"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={12} md={6} mb={1}>
          <Box
            sx={{
              border: "2px solid lightgrey",
              borderRadius: 2,
              p: 2,
              mb: 1,
              mx: { xs: 0, md: 1 },
              minHeight: {
                xs: "0px",
                sm: "0px",
                md: "400px",
                lg: "400px",
                xl: "400px",
              },
            }}
          >
            <Grid container>
              <Grid item xs={12} sm={6} md={12} lg={6}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    fontSize: "18px",
                    color: "#3e3e3e",
                    fontWeight: 500,
                    letterSpacing: "0.5px",
                  }}
                >
                  Interview Attendance Rate
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={12} lg={6}>
                <SelectInput
                  value={selectedJob}
                  name="job"
                  onChange={(e) => setSelectedJob(e.target.value)}
                  options={JobList?.map((item) => ({
                    value: item?.JD_Id,
                    label: `${item?.JD_Title}`,
                  }))}
                  placeholder="Select a Job"
                />
              </Grid>
            </Grid>

            {/* Centered & responsive chart */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: data4.length < 1 ? 0 : 3,
              }}
            >
              {data4.length < 1 ? (
                <Box
                  sx={{
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "40px", background: "white" }}
                  >
                    No data to show
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    xl={5.5}
                    sx={{ position: "relative", bottom: "20px" }}
                  >
                    <ResponsiveContainer width="100%" minHeight={300}>
                      <PieChart>
                        <Pie
                          data={data4}
                          dataKey="ApplicationCount"
                          nameKey="StatusLabel"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={110}
                          label
                          paddingAngle={2}
                        >
                          {data4.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Grid>

                  <Grid item xs={12} md={12} lg={12} xl={6.5}>
                    <Box sx={{ pl: { xs: 0, md: 2 } }}>
                      {data4?.map((entry, index) => (
                        <Grid
                          container
                          key={entry.status}
                          alignItems="center"
                          sx={{
                            mb: 1,
                            position: "relative",
                            left: {
                              xs: "14px",
                              sm: "12px",
                              md: "0px",
                              lg: "12px",
                            },
                            top: { xs: "0px", sm: "0px", md: "0px", lg: "8px" },
                          }}
                        >
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              backgroundColor: COLORS[index % COLORS.length],
                              borderRadius: "50%",
                              mr: 1,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: "400",
                              fontFamily: "Geist, sans-serif",
                            }}
                            variant="body2"
                          >
                            {entry.StatusLabel} ({entry.ApplicationCount})
                          </Typography>
                        </Grid>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={12} md={12} mb={1}>
          <Box
            sx={{
              border: "2px solid lightgrey",
              borderRadius: 2,
              p: 2,
              mb: 1,
              mx: { xs: 0, md: 1 },
              minHeight: {
                xs: "0px",
                sm: "0px",
                md: "400px",
                lg: "400px",
                xl: "400px",
              },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    fontSize: "18px",
                    color: "#3e3e3e",
                    fontWeight: 500,
                    letterSpacing: "0.5px",
                  }}
                >
                  Recruitment Process
                </Typography>
              </Grid>



              <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Recruitment Process
                </Typography>
                <SelectInput
                  value={processName}
                  name="job"
                  onChange={(e) => setProcessName(e.target.value)}
                  options={processList?.map((item) => ({
                    value: item?.RP_Id,
                    label: item?.RP_Name,
                  }))}
                  placeholder="Select a Process"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Recruitment Stage
                </Typography>
                <SelectInput
                  value={recruitmentStage}
                  onChange={(e) => setRecruitmentStage(e.target.value)}
                  options={RecruitmentStageslist?.map((item) => ({
                    value: item?.RPS_Id,
                    label: item?.RPS_Name,
                  }))}
                  placeholder="Select a Stage"
                  customStyle={{ width: "100%" }}
                  isDisabled={!processName}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Start Date
                </Typography>
                <TextInput
                  value={startDate}
                  type="date"
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder="Select Start Date"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  End Date
                </Typography>
                <TextInput
                  value={endDate}
                  type="date"
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder="Select End Date"
                />
              </Grid>

              <Grid item xs={12}>
               
                {data5?.length == 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px", // adjust as needed or use '100%' if parent has height
                      width: "100%",
                      marginTop: "60px",
                    }}
                  >
                    <Typography
                      color="text.secondary"
                      sx={{
                        fontSize: "40px",
                        background: "white",
                        p: 1,
                        borderRadius: 1,
                      }}
                    >
                      No data to show
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      position: "relative",
                      top: "15px",
                    }}
                  >
                    <ResponsiveContainer width="100%" minHeight={300}>
                      <BarChart
                        data={data5}
                        margin={{
                          top: 5,
                          right: 10,
                          left: data5?.every((item) => item.CompletedCount == 0)
                            ? -40
                            : 0,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          type="category"
                          dataKey="stage_name"
                          tick={{
                            fill: "#333",
                            fontSize: 14,
                            fontWeight: "bold",
                            fontFamily: "Geist, sans-serif",
                          }}
                        />
                        <YAxis
                          type="number"
                          tick={{
                            fill: "#333",
                            fontSize: 14,
                            fontWeight: "bold",
                            fontFamily: "Geist, sans-serif",
                          }}
                        />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="CompletedCount"
                          fill="#006CB8"
                          name="Completed Count"
                        />
                        <Bar
                          dataKey="InProcessCount"
                          fill="#FF9800"
                          name="In Process Count"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

const CandidateReportView = ({
  candidateStatData,
  candidateProcessData,
  countriesInProgress,
  depoyedByCountry,
  professionStats,
  interviewStats,
  JobList,
  selectedJob,
  setSelectedJob,

  processList,
  RecruitmentStageslist,

  processName,
  setProcessName,

  recruitmentStage,
  setRecruitmentStage,

  startDate,
  setStartDate,
  endDate,
  setEndDate,
}) => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        mb={1}
        px={1}
      >
        Candidate Reports
      </Typography>

      <Grid container spacing={2} p={1}>
        {candidateStatData.map((item, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={idx}>
            <StatCard
              title={item.title}
              value={item.value}
              Icon={item?.Icon}
              color={item?.color}
            />
          </Grid>
        ))}
      </Grid>

      <ApplicationsChart
        data1={countriesInProgress}
        data2={depoyedByCountry}
        data3={professionStats}
        data4={interviewStats}
        data5={candidateProcessData}
        JobList={JobList}
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        
        processList={processList}
        RecruitmentStageslist={RecruitmentStageslist}

        recruitmentStage={recruitmentStage}
        setRecruitmentStage={setRecruitmentStage}

        
        processName={processName}
        setProcessName={setProcessName}


        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
    </Box>
  );
};

export default CandidateReportView;
